import React, { useState, useEffect } from "react";
import { 
    X, Save, Edit2, Loader2, Music, 
    Play, Headphones 
} from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { MusicPageSettingServices } from "../../services/admin/MusicPageSettingServices";

export default function StreamingPlatformModal({ isOpen, onClose }) {
    const { showNotification } = useNotification();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({
        spotify_url: "",
        apple_music_url: "",
        soundcloud_url: "",
        audiomack_url: ""
    });

    const [originalData, setOriginalData] = useState({});

    useEffect(() => {
        if (isOpen) {
            const fetchSettings = async () => {
                try {
                    setIsLoading(true);
                    const response = await MusicPageSettingServices.getSettings();
                    if (response.status === "success") {
                        setFormData(response.data);
                        setOriginalData(response.data);
                    }
                } catch (error) {
                    showNotification({
                        type: "failed",
                        title: "Load Error",
                        message: "Failed to fetch streaming platform settings."
                    });
                } finally {
                    setIsLoading(false);
                }
            };
            fetchSettings();
        }
    }, [isOpen, showNotification]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const response = await MusicPageSettingServices.updateSettings(formData);
            if (response.status === "success") {
                showNotification({ 
                    type: "success", 
                    title: "Settings Saved", 
                    message: "Streaming platform links updated successfully." 
                });
                setOriginalData(formData);
                setIsEditing(false);
            } else {
                showNotification({ 
                    type: "failed", 
                    title: "Error", 
                    message: response.message || "Failed to update settings." 
                });
            }
        } catch (error) {
            showNotification({ 
                type: "failed", 
                title: "Error", 
                message: "An unexpected error occurred." 
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
    };

    if (!isOpen) return null;

    const inputClasses = "w-full pl-12 pr-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#222] text-sm focus:border-jazz-brown outline-none transition-colors disabled:opacity-75 disabled:cursor-not-allowed";
    const labelClasses = "block text-xs font-bold text-black dark:text-white uppercase tracking-widest mb-2";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="p-6 border-b border-black/5 dark:border-white/5 flex items-center justify-between bg-[#FAFAFA] dark:bg-[#1E1E1E]">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-jazz-brown/10 text-jazz-brown rounded-xl">
                            <Headphones size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                                Streaming Platforms
                            </h2>
                            <p className="text-[10px] text-black dark:text-white/50 uppercase tracking-[0.2em] font-medium">Manage Music Page Links</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors">
                        <X size={20} className="text-black/40 dark:text-white/40" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8 max-h-[70vh] overflow-y-auto">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <Loader2 className="animate-spin text-jazz-brown" size={40} />
                            <p className="text-sm text-black/50 dark:text-white/40 font-serif italic">Loading settings...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSave} className="space-y-6">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest">Platform Links</h3>
                                {!isEditing && (
                                    <button
                                        type="button"
                                        onClick={() => setIsEditing(true)}
                                        className="flex items-center gap-1.5 text-jazz-brown hover:text-jazz-brown/80 text-[10px] font-bold uppercase tracking-widest transition-colors"
                                    >
                                        <Edit2 size={12} />
                                        Edit Links
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-5">
                                {/* Spotify */}
                                <div>
                                    <label className={labelClasses}>Spotify URL</label>
                                    <div className="relative">
                                        <Play className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1DB954]" size={18} />
                                        <input
                                            type="url"
                                            name="spotify_url"
                                            value={formData.spotify_url || ""}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                            className={inputClasses}
                                            placeholder="https://open.spotify.com/artist/..."
                                        />
                                    </div>
                                </div>

                                {/* Apple Music */}
                                <div>
                                    <label className={labelClasses}>Apple Music URL</label>
                                    <div className="relative">
                                        <Music className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FA243C]" size={18} />
                                        <input
                                            type="url"
                                            name="apple_music_url"
                                            value={formData.apple_music_url || ""}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                            className={inputClasses}
                                            placeholder="https://music.apple.com/..."
                                        />
                                    </div>
                                </div>

                                {/* SoundCloud */}
                                <div>
                                    <label className={labelClasses}>SoundCloud URL</label>
                                    <div className="relative">
                                        <Play className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FF5500]" size={18} />
                                        <input
                                            type="url"
                                            name="soundcloud_url"
                                            value={formData.soundcloud_url || ""}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                            className={inputClasses}
                                            placeholder="https://soundcloud.com/..."
                                        />
                                    </div>
                                </div>

                                {/* Audiomack */}
                                <div>
                                    <label className={labelClasses}>Audiomack URL</label>
                                    <div className="relative">
                                        <Headphones className="absolute left-4 top-1/2 -translate-y-1/2 text-[#629AA9]" size={18} />
                                        <input
                                            type="url"
                                            name="audiomack_url"
                                            value={formData.audiomack_url || ""}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                            className={inputClasses}
                                            placeholder="https://audiomack.com/..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {isEditing && (
                                <div className="flex items-center justify-end gap-3 pt-6 border-t border-black/5 dark:border-white/5">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-6 py-2.5 bg-gray-100 text-black dark:bg-[#2A2A2A] dark:text-white rounded-xl hover:bg-gray-200 dark:hover:bg-[#333] transition-colors text-[10px] font-bold tracking-widest uppercase"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-xl hover:bg-jazz-brown transition-colors text-[10px] font-bold tracking-widest uppercase disabled:opacity-50"
                                    >
                                        {isSaving ? (
                                            <>
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={14} />
                                                Save Links
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </form>
                    )}
                </div>

                {/* Footer hint */}
                {!isEditing && (
                    <div className="px-8 py-4 bg-[#FAFAFA] dark:bg-[#1E1E1E] border-t border-black/5 dark:border-white/5">
                        <p className="text-[10px] text-black/40 dark:text-white/30 text-center font-medium italic">
                            These links are displayed in the "Listen on your favorite platform" section of the Music page.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
